import {
  CaretRightOutlined,
  CheckCircleFilled,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Empty, Input, Segmented, Select, Tag, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { EMPTY_GUID, type PdfFieldMapping, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { BmoModal } from '@/components/BmoModal';
import { PageHeader } from '@/components/PageHeader';
import { StatusTag } from '@/components/StatusTag';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { PdfAcroViewer } from './PdfAcroViewer';
import {
  collectMappableFields,
  collectPdfEntryOptions,
  type MappableField,
} from './pdfFieldMapping';
import { useEditorLock } from './useEditorLock';

/** A row carries a mapping when it has at least one AcroForm handle. */
function isMapped(f: MappableField): boolean {
  return f.kind === 'column' ? Boolean(f.internalName) : f.mappings.length > 0;
}

/** Identity of one AcroForm box a mapping targets — document, name, instance. */
function boxKey(m: PdfFieldMapping): string {
  return `${m.pdfEntryId}|${m.instanceIndex ?? ''}|${m.acroFieldName}`;
}

type GroupEntry = { type: 'group'; key: string; label: string; items: MappableField[] };
type ListEntry = { type: 'row'; item: MappableField } | GroupEntry;

export function PdfFieldMappingPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { oid } = useRoles();
  const isFr = i18n.language.startsWith('fr');

  const goToDetails = useCallback(
    () => navigate(id ? `/originator/workflows/${id}` : '/originator/dashboard'),
    [navigate, id],
  );
  const { workflow, loading, lockBlock, patchWorkflow } = useEditorLock(id, oid, t, goToDetails);

  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Repeatable groups are collapsed by default (this Set holds the EXPANDED ones),
  // so a section/row that expands into many indexed rows doesn't flood the list.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedPdfId, setSelectedPdfId] = useState<string | null>(null);
  const [lang, setLang] = useState<'en' | 'fr'>(isFr ? 'fr' : 'en');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const mappable = useMemo<MappableField[]>(
    () => (workflow ? collectMappableFields(workflow, isFr) : []),
    [workflow, isFr],
  );
  const pdfEntries = useMemo(() => (workflow ? collectPdfEntryOptions(workflow) : []), [workflow]);
  const entryLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of pdfEntries) map.set(e.id, e.label);
    return map;
  }, [pdfEntries]);

  // Default the PDF selector to the first uploaded entry once the workflow loads.
  useEffect(() => {
    const first = pdfEntries[0];
    if (selectedPdfId === null && first) setSelectedPdfId(first.id);
  }, [pdfEntries, selectedPdfId]);

  const activeField = useMemo(
    () => mappable.find((f) => f.key === activeKey) ?? null,
    [mappable, activeKey],
  );
  const selectedEntry = pdfEntries.find((e) => e.id === selectedPdfId) ?? null;
  const fileKey = selectedEntry
    ? lang === 'fr'
      ? selectedEntry.frFileKey
      : selectedEntry.enFileKey
    : null;

  // Does a scalar field's mapping apply to the currently-selected PDF? True for
  // a mapping on this exact entry, and for a legacy "all documents" mapping
  // (EMPTY_GUID) so pre-per-entry configs still read as mapped in every PDF.
  const appliesToSelectedPdf = useCallback(
    (pdfEntryId: string) => pdfEntryId === selectedPdfId || pdfEntryId === EMPTY_GUID,
    [selectedPdfId],
  );

  // AcroField name → labels of the OTHER fields mapped to it IN THE SELECTED PDF,
  // for the viewer's "already mapped" styling. Mapping is many-to-many, so a box
  // can carry several; `mappable` is in workflow order, so the list order is the
  // waterfall fill order. The active field's own mapping is excluded so its boxes
  // read as "selected". Scalar fields map per-entry; repeatable columns are
  // document-agnostic so they apply to any selected PDF.
  const assignedNames = useMemo(() => {
    const map = new Map<string, string[]>();
    const add = (name: string, label: string) => {
      const list = map.get(name);
      if (list) list.push(label);
      else map.set(name, [label]);
    };
    for (const f of mappable) {
      if (f.key === activeKey) continue;
      if (f.kind === 'field' || f.kind === 'option') {
        for (const m of f.mappings) {
          if (appliesToSelectedPdf(m.pdfEntryId)) add(m.acroFieldName, f.label);
        }
      } else if (f.internalName) {
        add(f.internalName, f.label);
      }
    }
    return map;
  }, [mappable, activeKey, appliesToSelectedPdf]);

  // Fill order, per AcroForm box: every field mapped to the same (PDF entry,
  // AcroForm name, instance), in workflow order. At generation time they fill
  // the box waterfall-style — each writing over the last — so a field's position
  // in this list is where its answer lands in that sequence, and the final entry
  // is the one that survives. Keyed on the raw mapping triple, NOT filtered by
  // the selected PDF, since the tags in the left list show mappings for every
  // document at once.
  const claimantsByBox = useMemo(() => {
    const map = new Map<string, MappableField[]>();
    for (const f of mappable) {
      if (f.kind === 'column') continue;
      for (const m of f.mappings) {
        const key = boxKey(m);
        const list = map.get(key);
        if (list) list.push(f);
        else map.set(key, [f]);
      }
    }
    return map;
  }, [mappable]);

  // The active field's AcroForm names in the selected PDF — the viewer renders
  // these boxes as "selected".
  const activeNames = useMemo(() => {
    const set = new Set<string>();
    if (!activeField) return set;
    if (activeField.kind === 'field' || activeField.kind === 'option') {
      for (const m of activeField.mappings) {
        if (appliesToSelectedPdf(m.pdfEntryId)) set.add(m.acroFieldName);
      }
    } else if (activeField.internalName) {
      set.add(activeField.internalName);
    }
    return set;
  }, [activeField, appliesToSelectedPdf]);

  const mappedCount = mappable.filter(isMapped).length;
  const editable =
    lockBlock === null && (workflow?.status === 'Draft' || workflow?.status === 'Rejected');

  // Fold the flat list into an ordered mix of standalone rows and collapsible
  // groups (repeatable rows / sections). Rows sharing a `groupKey` collect under
  // the group anchored at its first appearance; non-repeatable rows stay inline.
  const listEntries = useMemo<ListEntry[]>(() => {
    const entries: ListEntry[] = [];
    const groupIndex = new Map<string, number>();
    for (const f of mappable) {
      if (f.groupKey === undefined) {
        entries.push({ type: 'row', item: f });
        continue;
      }
      const at = groupIndex.get(f.groupKey);
      if (at === undefined) {
        groupIndex.set(f.groupKey, entries.length);
        entries.push({
          type: 'group',
          key: f.groupKey,
          label: f.groupLabel ?? f.label,
          items: [f],
        });
      } else {
        (entries[at] as GroupEntry).items.push(f);
      }
    }
    return entries;
  }, [mappable]);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handlePick = useCallback(
    async (fieldName: string) => {
      if (!id || !activeField || !editable) return;
      setSaving(true);
      try {
        let removed: boolean;
        if (activeField.kind === 'field' || activeField.kind === 'option') {
          // Scalar field OR choice option: toggle a per-PDF mapping against the
          // selected entry. Options additionally carry their option value.
          if (!selectedPdfId) return;
          removed = activeField.mappings.some(
            (m) => m.pdfEntryId === selectedPdfId && m.acroFieldName === fieldName,
          );
          if (activeField.kind === 'option') {
            await workflowsApi.toggleOptionPdfMapping(
              id,
              activeField.stepId,
              activeField.sectionId,
              activeField.rowId,
              activeField.columnId,
              {
                optionValue: activeField.optionValue,
                pdfEntryId: selectedPdfId,
                acroFieldName: fieldName,
                instanceIndex: activeField.instanceIndex,
              },
            );
          } else {
            await workflowsApi.togglePdfMapping(
              id,
              activeField.stepId,
              activeField.sectionId,
              activeField.rowId,
              activeField.columnId,
              {
                pdfEntryId: selectedPdfId,
                acroFieldName: fieldName,
                instanceIndex: activeField.instanceIndex,
              },
            );
          }
        } else {
          // Repeatable column: scalar 1:1 per row. Clicking the mapped box clears it.
          const next = activeField.internalName === fieldName ? null : fieldName;
          removed = next === null;
          await workflowsApi.setFieldInternalName(
            id,
            activeField.stepId,
            activeField.sectionId,
            activeField.rowId,
            activeField.columnId,
            {
              internalName: next,
              repeatableColumnId: activeField.repeatableColumnId,
              rowIndex: activeField.rowIndex,
            },
          );
        }
        // Refetch so the whole aggregate (and lock info) stays fresh; the flatten
        // + assigned-name maps recompute from it.
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        // Boxes are many-to-many: say so when this click added the active field
        // alongside others, since the fill then waterfalls in workflow order.
        const alsoMapped = assignedNames.get(fieldName)?.length ?? 0;
        message.success(
          removed
            ? t('pdfMapping.cleared')
            : alsoMapped > 0
              ? t('pdfMapping.mappedShared', { total: alsoMapped + 1 })
              : t('pdfMapping.mapped'),
        );
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, activeField, editable, selectedPdfId, patchWorkflow, assignedNames, t],
  );

  // Remove one already-saved mapping directly from its tag in the left list —
  // no need to re-select the field or the matching PDF. Addresses the passed-in
  // field + mapping (not the active field / selected PDF), and reuses the same
  // idempotent toggle endpoints as handlePick: since the pair exists, toggling
  // clears it. Passing the mapping's own pdfEntryId also clears legacy
  // EMPTY_GUID ("all documents") mappings correctly.
  const handleRemove = useCallback(
    async (field: MappableField, mapping?: PdfFieldMapping) => {
      if (!id || !editable) return;
      setSaving(true);
      try {
        if (field.kind === 'option') {
          if (!mapping) return;
          await workflowsApi.toggleOptionPdfMapping(
            id,
            field.stepId,
            field.sectionId,
            field.rowId,
            field.columnId,
            {
              optionValue: field.optionValue,
              pdfEntryId: mapping.pdfEntryId,
              acroFieldName: mapping.acroFieldName,
              instanceIndex: mapping.instanceIndex,
            },
          );
        } else if (field.kind === 'field') {
          if (!mapping) return;
          await workflowsApi.togglePdfMapping(
            id,
            field.stepId,
            field.sectionId,
            field.rowId,
            field.columnId,
            {
              pdfEntryId: mapping.pdfEntryId,
              acroFieldName: mapping.acroFieldName,
              instanceIndex: mapping.instanceIndex,
            },
          );
        } else {
          // Repeatable column: scalar 1:1 per row, cleared by nulling this row's handle.
          await workflowsApi.setFieldInternalName(
            id,
            field.stepId,
            field.sectionId,
            field.rowId,
            field.columnId,
            {
              internalName: null,
              repeatableColumnId: field.repeatableColumnId,
              rowIndex: field.rowIndex,
            },
          );
        }
        const fresh = await workflowsApi.get(id);
        patchWorkflow(fresh);
        message.success(t('pdfMapping.cleared'));
      } catch (err) {
        handleApiError(err, t, t('pdfMapping.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [id, editable, patchWorkflow, t],
  );

  // One selectable field row. `inGroup` indents it under a collapsible header and
  // shortens the label to "Item N · <field>" (the group header carries the rest).
  const renderRow = (f: MappableField, inGroup: boolean) => {
    const active = f.key === activeKey;
    const remainder =
      inGroup && f.groupLabel && f.label.startsWith(`${f.groupLabel} › `)
        ? f.label.slice(f.groupLabel.length + 3)
        : f.label;
    const display =
      inGroup && f.instanceIndex != null
        ? `${t('pdfMapping.itemLabel', { n: f.instanceIndex })} · ${remainder}`
        : remainder;
    const padLeft = 14 + (inGroup ? 14 : 0) + (f.kind === 'option' ? 14 : 0);
    return (
      <button
        type="button"
        key={f.key}
        onClick={() => setActiveKey(f.key)}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          padding: `10px 14px 10px ${padLeft}px`,
          border: 'none',
          borderBottom: '1px solid #f5f5f5',
          borderLeft: active ? '3px solid #0179C1' : '3px solid transparent',
          background: active ? '#eff8ff' : '#fff',
          cursor: 'pointer',
        }}
      >
        <div style={{ fontSize: 13, color: '#111827', marginBottom: 4 }}>{display}</div>
        {f.kind !== 'column' ? (
          f.mappings.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {f.mappings.map((m, i) => {
                // Shared box → surface where this field sits in the waterfall, so
                // the originator can see whether a later field overwrites it.
                const claimants = claimantsByBox.get(boxKey(m)) ?? [];
                const shared = claimants.length > 1;
                const order = claimants.indexOf(f) + 1;
                const label =
                  m.pdfEntryId === EMPTY_GUID
                    ? m.acroFieldName
                    : `${entryLabelById.get(m.pdfEntryId) ?? '?'} · ${m.acroFieldName}`;
                const tag = (
                  <Tag
                    key={`${m.pdfEntryId}:${m.acroFieldName}:${i}`}
                    color="green"
                    icon={<CheckCircleFilled />}
                    closable={editable && !saving}
                    onClose={(e) => {
                      // Prevent antd's optimistic DOM removal (we re-render from the
                      // server refetch) and the row button's own onClick from firing.
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(f, m);
                    }}
                    style={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginInlineEnd: 0,
                    }}
                  >
                    {shared ? `${label} · ${t('pdfMapping.fillOrder', { order })}` : label}
                  </Tag>
                );
                return shared ? (
                  <Tooltip
                    key={`${m.pdfEntryId}:${m.acroFieldName}:${i}`}
                    title={t('pdfMapping.sharedBoxTooltip', {
                      order,
                      total: claimants.length,
                      fields: claimants
                        .filter((c) => c !== f)
                        .map((c) => c.label)
                        .join(' · '),
                    })}
                  >
                    {tag}
                  </Tooltip>
                ) : (
                  tag
                );
              })}
            </div>
          ) : (
            <Tag>{t('pdfMapping.unmapped')}</Tag>
          )
        ) : f.internalName ? (
          <Tag
            color="green"
            icon={<CheckCircleFilled />}
            closable={editable && !saving}
            onClose={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleRemove(f);
            }}
            style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {f.internalName}
          </Tag>
        ) : (
          <Tag>{t('pdfMapping.unmapped')}</Tag>
        )}
      </button>
    );
  };

  if (loading || !workflow) {
    return <div style={{ minHeight: 460 }} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
      <PageHeader
        title={t('pdfMapping.title')}
        titleSuffix={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <Tooltip title={t('pdfMapping.helpAria')}>
              <button
                type="button"
                aria-label={t('pdfMapping.helpAria')}
                onClick={() => setHelpOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6b7280',
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                <QuestionCircleOutlined />
              </button>
            </Tooltip>
            <StatusTag status={workflow.status} />
          </span>
        }
        onBack={goToDetails}
      />

      <BmoModal
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        title={t('pdfMapping.helpTitle')}
        footer={
          <Button type="primary" onClick={() => setHelpOpen(false)}>
            {t('common.gotIt')}
          </Button>
        }
      >
        <p style={{ margin: 0, lineHeight: 1.6 }}>{t('pdfMapping.helpIntro')}</p>
        <div
          style={{
            marginTop: 16,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#eff8ff',
            border: '1px solid #bae0fd',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#0b4a6f' }}>
            {t('pdfMapping.helpSharedNoteTitle')}
          </div>
          <p style={{ margin: 0, color: '#0b4a6f', lineHeight: 1.6 }}>
            {t('pdfMapping.helpSharedNote')}
          </p>
        </div>
        <div
          style={{
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 10,
            background: '#fff7ed',
            border: '1px solid #fde68a',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#92400e' }}>
            {t('pdfMapping.helpCheckboxNoteTitle')}
          </div>
          <p style={{ margin: 0, color: '#92400e', lineHeight: 1.6 }}>
            {t('pdfMapping.helpCheckboxNote')}
          </p>
        </div>
      </BmoModal>

      {!editable && (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fff7ed',
            border: '1px solid #fde68a',
            color: '#92400e',
            fontSize: 13,
          }}
        >
          {t('pdfMapping.readOnly')}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0, height: '90vh' }}>
        {/* Left: form fields to map */}
        <div
          style={{
            width: 360,
            flexShrink: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {t('pdfMapping.fieldsTitle')}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {t('pdfMapping.mappedProgress', { mapped: mappedCount, total: mappable.length })}
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {mappable.length === 0 ? (
              <div style={{ padding: 24 }}>
                <Empty description={t('pdfMapping.noFields')} />
              </div>
            ) : (
              listEntries.map((entry) => {
                if (entry.type === 'row') return renderRow(entry.item, false);
                const expanded = expandedGroups.has(entry.key);
                const groupMapped = entry.items.filter(isMapped).length;
                return (
                  <div key={`group:${entry.key}`}>
                    <Tooltip
                      title={t(
                        expanded
                          ? 'workflow.builder.section.collapse'
                          : 'workflow.builder.section.expand',
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleGroup(entry.key)}
                        aria-expanded={expanded}
                        aria-label={t(
                          expanded
                            ? 'workflow.builder.section.collapse'
                            : 'workflow.builder.section.expand',
                        )}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 14px',
                          border: 'none',
                          borderBottom: '1px solid #f0f0f0',
                          borderLeft: '3px solid transparent',
                          background: '#fafafa',
                          cursor: 'pointer',
                        }}
                      >
                        <CaretRightOutlined
                          style={{
                            fontSize: 10,
                            color: '#6b7280',
                            transition: 'transform 140ms ease',
                            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#111827',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {entry.label}
                        </span>
                        <span style={{ fontSize: 12, color: '#6b7280', flexShrink: 0 }}>
                          {t('pdfMapping.groupProgress', {
                            mapped: groupMapped,
                            total: entry.items.length,
                          })}
                        </span>
                      </button>
                    </Tooltip>
                    {expanded && entry.items.map((f) => renderRow(f, true))}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right: PDF viewer */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            overflow: 'hidden',
            height: '80vh',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 14px',
              borderBottom: '1px solid #f0f0f0',
              flexWrap: 'wrap',
            }}
          >
            <Select
              style={{ minWidth: 220 }}
              placeholder={t('pdfMapping.selectPdf')}
              value={selectedPdfId ?? undefined}
              onChange={setSelectedPdfId}
              options={pdfEntries.map((e) => ({ value: e.id, label: e.label }))}
            />
            <Segmented
              value={lang}
              onChange={(v) => setLang(v as 'en' | 'fr')}
              options={[
                { label: t('pdfMapping.english'), value: 'en' },
                { label: t('pdfMapping.french'), value: 'fr' },
              ]}
            />
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#6b7280' }} />}
              placeholder={t('pdfMapping.searchAcro')}
              style={{ maxWidth: 260 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div
              style={{
                marginLeft: 'auto',
                fontSize: 12,
                color: activeField ? '#0179C1' : '#6b7280',
              }}
            >
              {activeField
                ? t('pdfMapping.activeHint', { field: activeField.label })
                : t('pdfMapping.selectFieldFirst')}
            </div>
          </div>
          <div style={{ flex: 1, overflow: 'auto', background: '#f3f4f6', padding: 20 }}>
            {pdfEntries.length === 0 ? (
              <Empty description={t('pdfMapping.noPdfs')} style={{ marginTop: 60 }} />
            ) : !fileKey ? (
              <Empty description={t('pdfMapping.noFileForLang')} style={{ marginTop: 60 }} />
            ) : (
              <PdfAcroViewer
                // Remount on document / language change so pdf.js state resets cleanly.
                key={`${selectedPdfId}:${lang}`}
                templateId={workflow.id}
                fileKey={fileKey}
                search={search}
                activeNames={activeNames}
                assignedNames={assignedNames}
                disabled={!editable || !activeField || saving}
                onPick={handlePick}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
